/**
 * VÉRAULT · Concierge Desk — Inbound Replies panel (Phase 1)
 * Feb 2026
 *
 * Lean MVP surface. NOT a full inbox — just a triage list of new
 * inbound client/prospect replies + email bounces detected via Nylas
 * webhooks. Anchored inside the existing Concierge page above the
 * thread list. Reuses the existing design system (Surface, EditorialRule,
 * StatusTag, PortalButton).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, MailOpen, Archive, RefreshCw, ExternalLink, ChevronDown, ChevronUp, AlertTriangle, User, Plane } from "lucide-react";
import { Surface, EditorialRule, StatusTag, PortalButton, api } from "../../index";

const FILTERS = [
  { id: "unread", label: "Unread" },
  { id: "possible", label: "Possible Matches" },
  { id: "reviewed", label: "Reviewed" },
  { id: "archived", label: "Archived" },
];

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function InboundRepliesPanel() {
  const [collapsed, setCollapsed] = useState(false);
  const [status, setStatus] = useState("unread");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ unread: 0, reviewed: 0, archived: 0 });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/inbox-alerts?status=${status}&limit=50`);
      setRows(data.items || []);
      setCounts(data.counts || {});
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 45000);  // gentle 45s poll
    return () => clearInterval(t);
  }, [load]);

  const patch = async (id, newStatus) => {
    try {
      await api.patch(`/inbox-alerts/${id}`, { status: newStatus });
      await load();
    } catch { /* noop */ }
  };

  const total = counts.unread || 0;

  return (
    <Surface
      variant="paper"
      padded
      className="!p-0 overflow-hidden"
      data-testid="inbound-replies-panel"
    >
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-white/50 transition-colors"
        data-testid="inbound-replies-toggle"
      >
        <div className="flex items-start gap-3 min-w-0">
          <span className="inline-flex items-center justify-center size-8 rounded-full bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))] shrink-0">
            <Mail className="size-3.5" strokeWidth={1.5} />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">
                Inbound Replies · Needs Attention
              </p>
              {total > 0 && (
                <span
                  className="inline-flex items-center justify-center px-1.5 min-w-[18px] h-[18px] rounded-full bg-[hsl(var(--foreground))] text-[hsl(var(--primary-foreground))] font-mono text-[10px]"
                  data-testid="inbound-replies-count"
                >
                  {total}
                </span>
              )}
            </div>
            <p className="mt-0.5 font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))] truncate">
              {total === 0
                ? "No client replies waiting."
                : `${total} client repl${total === 1 ? "y" : "ies"} to review · detected via Gmail.`}
            </p>
          </div>
        </div>
        {collapsed
          ? <ChevronDown className="size-4 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
          : <ChevronUp className="size-4 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />}
      </button>

      <AnimatePresence initial={false}>
        {!collapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.24 }}
            className="overflow-hidden border-t border-[hsl(var(--border))]/60"
          >
            <div className="px-5 py-4 space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                {FILTERS.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setStatus(f.id)}
                    className={
                      "px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.20em] transition-colors " +
                      (status === f.id
                        ? "bg-[hsl(var(--foreground))] text-[hsl(var(--primary-foreground))]"
                        : "bg-white text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] border border-[hsl(var(--border))]")
                    }
                    data-testid={`inbound-filter-${f.id}`}
                  >
                    {f.label}
                    {f.id === "unread" && counts.unread > 0 && (
                      <span className="ml-1.5 opacity-80">· {counts.unread}</span>
                    )}
                    {f.id === "possible" && counts.possible > 0 && (
                      <span className="ml-1.5 opacity-80">· {counts.possible}</span>
                    )}
                  </button>
                ))}
                <div className="flex-1" />
                <button
                  type="button"
                  onClick={load}
                  className="inline-flex items-center gap-1 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.20em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
                  data-testid="inbound-refresh"
                  title="Refresh"
                >
                  <RefreshCw className={"size-3 " + (loading ? "animate-spin" : "")} strokeWidth={1.5} />
                  Refresh
                </button>
              </div>

              {rows.length === 0 && (
                <div className="py-8 text-center">
                  <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">
                    {status === "unread"
                      ? "All caught up — no inbound replies waiting."
                      : status === "possible"
                        ? "No possible matches waiting for review."
                        : "Nothing here."}
                  </p>
                </div>
              )}

              {rows.map((r) => (
                <InboundRow
                  key={r.id}
                  row={r}
                  expanded={!!expanded[r.id]}
                  onExpand={() => setExpanded((prev) => ({ ...prev, [r.id]: !prev[r.id] }))}
                  onReview={() => patch(r.id, "reviewed")}
                  onArchive={() => patch(r.id, "archived")}
                  onReopen={() => patch(r.id, "unread")}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Surface>
  );
}

function InboundRow({ row, expanded, onExpand, onReview, onArchive, onReopen }) {
  const isBounce = row.kind === "bounce";
  const unread = row.status === "unread";
  return (
    <div
      className={
        "rounded-md border p-3 transition-colors " +
        (unread
          ? "border-[hsl(var(--accent-gold))]/30 bg-[hsl(var(--accent-gold))]/[0.06]"
          : "border-[hsl(var(--border))] bg-white/60")
      }
      data-testid="inbound-row"
      data-alert-id={row.id}
      data-status={row.status}
    >
      <div className="flex items-start gap-3">
        <span className={
          "inline-flex items-center justify-center size-7 rounded-full shrink-0 " +
          (isBounce ? "bg-red-50 text-red-700" : "bg-[hsl(var(--foreground))] text-[hsl(var(--primary-foreground))]")
        }>
          {isBounce ? <AlertTriangle className="size-3.5" strokeWidth={1.5} /> : (unread ? <Mail className="size-3.5" strokeWidth={1.5} /> : <MailOpen className="size-3.5" strokeWidth={1.5} />)}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <p className="font-serif text-[14px] text-[hsl(var(--foreground))] leading-tight">
              {row.sender_name || row.sender_email || (isBounce ? "Delivery bounce" : "Unknown sender")}
            </p>
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
              {relTime(row.received_at)}
            </span>
            {isBounce && <StatusTag tone="danger">Bounce</StatusTag>}
            {row.level === 3 && <StatusTag tone="muted">Possible match</StatusTag>}
            {row.urgent && row.level === 2 && <StatusTag tone="warning">Urgent · trip active</StatusTag>}
            {!row.matched_client_id && !row.matched_prospect_id && !row.matched_vendor_name && !isBounce && row.level >= 3 && <StatusTag tone="muted">Unmatched</StatusTag>}
          </div>

          <p className="mt-0.5 font-body text-[12.5px] text-[hsl(var(--foreground))] truncate">
            {row.subject || "(no subject)"}
          </p>

          {row.ai_summary && (
            <p className="mt-1 font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))] leading-relaxed">
              {row.ai_summary}
            </p>
          )}

          {(row.matched_client_name || row.matched_prospect_name || row.matched_vendor_name || row.matched_trip_label) && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {row.matched_client_name && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-[hsl(var(--border))] font-mono text-[9.5px] uppercase tracking-[0.18em] text-[hsl(var(--foreground))]">
                  <User className="size-2.5" strokeWidth={1.5} />{row.matched_client_name}
                </span>
              )}
              {!row.matched_client_name && row.matched_prospect_name && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-[hsl(var(--border))] font-mono text-[9.5px] uppercase tracking-[0.18em] text-[hsl(var(--foreground))]">
                  <User className="size-2.5" strokeWidth={1.5} />{row.matched_prospect_name} · Prospect
                </span>
              )}
              {row.matched_vendor_name && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-[hsl(var(--border))] font-mono text-[9.5px] uppercase tracking-[0.18em] text-[hsl(var(--foreground))]">
                  {row.matched_vendor_name} · Vendor
                </span>
              )}
              {row.matched_trip_label && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-[hsl(var(--border))] font-mono text-[9.5px] uppercase tracking-[0.18em] text-[hsl(var(--foreground))]">
                  <Plane className="size-2.5" strokeWidth={1.5} />{row.matched_trip_label}
                </span>
              )}
            </div>
          )}

          {expanded && row.snippet && (
            <p className="mt-2 font-body text-[12px] text-[hsl(var(--foreground-muted))] leading-relaxed whitespace-pre-wrap max-h-40 overflow-auto">
              {row.snippet}
            </p>
          )}
        </div>
      </div>

      <div className="mt-2 flex items-center gap-2 flex-wrap justify-end">
        {row.snippet && (
          <button
            type="button"
            onClick={onExpand}
            className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] px-2 py-1"
            data-testid="inbound-row-expand"
          >
            {expanded ? "Hide preview" : "Show preview"}
          </button>
        )}
        {row.gmail_link ? (
          <a
            href={row.gmail_link}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--accent-gold))] hover:text-[hsl(var(--foreground))] px-2 py-1"
            data-testid="inbound-row-gmail"
          >
            <ExternalLink className="size-3" strokeWidth={1.5} />
            Open in Gmail
          </a>
        ) : (
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]/60 px-2 py-1" title="Gmail deep-link not available for this event">
            Open in Gmail unavailable
          </span>
        )}
        {row.status === "unread" && (
          <PortalButton size="sm" variant="ghost" onClick={onReview} data-testid="inbound-row-review">
            Mark reviewed
          </PortalButton>
        )}
        {row.status === "reviewed" && (
          <PortalButton size="sm" variant="ghost" onClick={onReopen} data-testid="inbound-row-reopen">
            Re-open
          </PortalButton>
        )}
        {row.status !== "archived" ? (
          <PortalButton size="sm" variant="ghost" icon={<Archive className="size-3.5" strokeWidth={1.5} />} onClick={onArchive} data-testid="inbound-row-archive">
            Archive
          </PortalButton>
        ) : (
          <PortalButton size="sm" variant="ghost" onClick={onReopen} data-testid="inbound-row-unarchive">
            Restore
          </PortalButton>
        )}
      </div>
    </div>
  );
}
